/**
 * GoldenWings Calendar Page
 * Flight school scheduling calendar
 */

import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { message, Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLayoutConfig } from '@/components/common/ProtectedLayout';
import {
  CalendarHeader,
  type CalendarView,
} from '@/components/features/goldenwings/calendar/CalendarHeader';
import { MultiDayVerticalView } from '@/components/features/goldenwings/calendar/MultiDayVerticalView';
import { MonthlyView } from '@/components/features/goldenwings/calendar/MonthlyView';
import { YearView } from '@/components/features/goldenwings/calendar/YearView';
import { YearlyAgendaView } from '@/components/features/goldenwings/calendar/YearlyAgendaView';
import {
  ResourceFilterPanel,
  type FilterGroupCollapsedState,
} from '@/components/features/goldenwings/calendar/ResourceFilterPanel';
import { HorizontalView } from '@/components/features/goldenwings/calendar/HorizontalView';
import { calculateMondayOffset } from '@/components/features/goldenwings/calendar/utils/multiDayHelpers';
import { useCalendars, useEvents, useResources } from '@/hooks/useCalendars';
import { useCalendarSocket } from '@/hooks/useCalendarSocket';
import { calendarsService } from '@/api/services/calendars.service';
import type { CalendarEvent } from '@/api/types/calendars.types';
import {
  useCalendarSettings,
  useCalendarFilters,
  useSaveCalendarSettings,
  useSaveCalendarFilters,
} from '@/hooks/useCalendar';
import { EventFormModal } from '@/components/features/goldenwings/calendar/EventFormModal';
import './CalendarPage.css';

const FILTERS_STORAGE_KEY = 'goldenwings-calendar-filters';

export const GoldenWingsCalendarPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setNoPadding } = useLayoutConfig();

  // Load persistent settings and filters from backend
  const { data: savedSettings, isLoading: loadingSettings } =
    useCalendarSettings();
  const { data: savedFilters, isLoading: loadingFilters } =
    useCalendarFilters();
  const saveSettingsMutation = useSaveCalendarSettings();
  const saveFiltersMutation = useSaveCalendarFilters();

  const [view, setView] = useState<CalendarView>('week-new');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [showDayNight, setShowDayNight] = useState<boolean>(
    savedSettings?.show_day_night ?? true
  );
  const [showWeather, setShowWeather] = useState<boolean>(
    savedSettings?.show_weather ?? true
  );
  const [collapsedGroups, setCollapsedGroups] =
    useState<FilterGroupCollapsedState>({});
  const [initializedFromBackend, setInitializedFromBackend] = useState(false);

  // Event form modal state
  const [eventFormModalOpen, setEventFormModalOpen] = useState(false);
  const [eventFormStartTime, setEventFormStartTime] = useState<
    Date | undefined
  >();

  // Debug logging
  useEffect(() => {
    console.log('[GoldenWings] Component mounted!');
  }, []);

  useEffect(() => {
    console.log('[GoldenWings] Settings state:', {
      savedSettings,
      loadingSettings,
    });
  }, [savedSettings, loadingSettings]);

  useEffect(() => {
    console.log('[GoldenWings] Filters state:', {
      savedFilters,
      loadingFilters,
    });
  }, [savedFilters, loadingFilters]);

  // Set noPadding for calendar page
  useEffect(() => {
    setNoPadding(true);
    return () => setNoPadding(false);
  }, [setNoPadding]);

  // Fetch calendars
  const { data: calendars = [], isLoading: loadingCalendars } = useCalendars();

  // Mutation for updating events (drag & drop)
  const updateEventMutation = useMutation({
    mutationFn: ({
      eventId,
      startTime,
      endTime,
    }: {
      eventId: string;
      startTime: string;
      endTime: string;
    }) =>
      calendarsService.updateEvent(eventId, {
        start_time: startTime,
        end_time: endTime,
      }),
    onSuccess: () => {
      // Invalidate events query to refetch
      queryClient.invalidateQueries({ queryKey: ['calendars', 'events'] });
      message.success('Event updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating event:', error);
      message.error(error?.response?.data?.detail || 'Failed to update event');
      // Invalidate to revert optimistic update
      queryClient.invalidateQueries({ queryKey: ['calendars', 'events'] });
    },
  });

  // Use first calendar (or create one if none exists)
  const calendarId = calendars[0]?.id;

  // WebSocket real-time updates
  useCalendarSocket({
    enabled: !!calendarId,
    calendarIds: calendarId ? [calendarId] : [],
    selectedResourceIds,
    onEventCreated: (data) => {
      console.log('📅 Event created by another user:', data.event.title);
      // Optionally: message.info(`New event: ${data.event.title}`);
    },
    onEventUpdated: (data) => {
      console.log('📅 Event updated by another user:', data.event.title);
      // Optionally: message.info(`Event updated: ${data.event.title}`);
    },
    onEventDeleted: (data) => {
      console.log('📅 Event deleted by another user:', data.event_id);
      // Optionally: message.warning('Event deleted');
    },
  });

  // Calculate date range for event counts (simple start/end dates for resource query)
  const eventCountDateRange = useMemo(() => {
    const current = dayjs(currentDate);
    let start: string;
    let end: string;

    switch (view) {
      case 'day-h-1h':
      case '1day':
        start = current.format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '2day':
      case 'day-h-2d':
        start = current.format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '3day':
      case 'day-h-3d':
        start = current.subtract(1, 'day').format('YYYY-MM-DD');
        end = current.add(1, 'day').format('YYYY-MM-DD');
        break;
      case '7day':
      case 'week-h':
        start = current.subtract(1, 'day').format('YYYY-MM-DD');
        end = current.add(5, 'day').format('YYYY-MM-DD');
        break;
      case 'week-new': {
        const dayOfWeek = current.day();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = current.subtract(mondayOffset, 'day').format('YYYY-MM-DD');
        end = current
          .subtract(mondayOffset, 'day')
          .add(6, 'day')
          .format('YYYY-MM-DD');
        break;
      }
      case 'month':
        start = current.startOf('month').format('YYYY-MM-DD');
        end = current.endOf('month').format('YYYY-MM-DD');
        break;
      case 'year':
      case 'year-agenda':
        start = current.startOf('year').format('YYYY-MM-DD');
        end = current.endOf('year').format('YYYY-MM-DD');
        break;
      default:
        start = current.startOf('month').format('YYYY-MM-DD');
        end = current.endOf('month').format('YYYY-MM-DD');
    }

    return { start_date: start, end_date: end };
  }, [currentDate, view]);

  // Fetch all resources for filter tooltip (with event counts for current date range)
  const { data: airplanes = [], isLoading: loadingAirplanes } = useResources({
    resource_type: 'AIRPLANE',
    ...eventCountDateRange,
  });
  const { data: instructors = [], isLoading: loadingInstructors } =
    useResources({
      resource_type: 'INSTRUCTOR',
      ...eventCountDateRange,
    });
  const { data: pilots = [], isLoading: loadingPilots } = useResources({
    resource_type: 'PILOT',
    ...eventCountDateRange,
  });
  const { data: students = [], isLoading: loadingStudents } = useResources({
    resource_type: 'STUDENT',
    ...eventCountDateRange,
  });
  const { data: airports = [], isLoading: loadingAirports } = useResources({
    resource_type: 'AIRPORT',
    ...eventCountDateRange,
  });

  const loadingResources =
    loadingAirplanes ||
    loadingInstructors ||
    loadingPilots ||
    loadingStudents ||
    loadingAirports;

  // Combine all resources
  const allResources = useMemo(
    () => [...airplanes, ...instructors, ...pilots, ...students, ...airports],
    [airplanes, instructors, pilots, students, airports]
  );

  // Calculate effective resource IDs for filtering
  // Rule: If 0 or ALL items in a group are selected, no restriction for that group
  // Only send IDs for groups where a SUBSET is selected
  const effectiveResourceIds = useMemo(() => {
    const result: string[] = [];

    // Helper function to check if group should be filtered
    const addGroupIfSubset = (
      allInGroup: typeof airplanes,
      selectedIds: string[]
    ) => {
      const groupIds = allInGroup.map((r) => r.id);
      const selectedInGroup = selectedIds.filter((id) => groupIds.includes(id));
      // Only add if subset selected (not 0 and not all)
      if (
        selectedInGroup.length > 0 &&
        selectedInGroup.length < groupIds.length
      ) {
        result.push(...selectedInGroup);
      }
    };

    addGroupIfSubset(airports, selectedResourceIds);
    addGroupIfSubset(airplanes, selectedResourceIds);
    addGroupIfSubset(instructors, selectedResourceIds);
    addGroupIfSubset(pilots, selectedResourceIds);
    addGroupIfSubset(students, selectedResourceIds);

    return result;
  }, [airports, airplanes, instructors, pilots, students, selectedResourceIds]);

  // Calculate selected airports for weather and sun times
  const selectedAirports = useMemo(() => {
    return allResources.filter(
      (r) => r.resource_type === 'AIRPORT' && selectedResourceIds.includes(r.id)
    );
  }, [allResources, selectedResourceIds]);

  // Map selected airports with timezone info for multi-timezone display
  const selectedAirportsWithTimezone = useMemo(() => {
    return selectedAirports.map((airport) => ({
      id: airport.id,
      name: airport.name,
      timezone: airport.metadata?.timezone as string | undefined,
    }));
  }, [selectedAirports]);

  // Use selected airport ID for sun times (only when exactly 1 airport is selected)
  const selectedAirportId =
    selectedAirports.length === 1 ? selectedAirports[0].id : undefined;

  // Determine if day/night should be disabled (only enabled for exactly 1 airport)
  const dayNightDisabled = selectedAirports.length !== 1;
  const dayNightDisabledReason =
    selectedAirports.length === 0
      ? 'Izaberite aerodrom za prikaz izlaska/zalaska sunca'
      : 'Izlazak/zalazak sunca dostupan samo za jedan aerodrom';

  // Determine if weather should be disabled (only enabled for exactly 1 airport)
  const weatherDisabled = selectedAirports.length !== 1;
  const weatherDisabledReason =
    selectedAirports.length === 0
      ? 'Izaberite aerodrom za prikaz vremenske prognoze'
      : 'Vremenska prognoza dostupna samo za jedan aerodrom';

  // Get coordinates for weather forecast
  const weatherCoordinates = useMemo(() => {
    if (selectedAirports.length === 1) {
      const airport = selectedAirports[0];
      return {
        latitude: airport.metadata?.latitude as number | undefined,
        longitude: airport.metadata?.longitude as number | undefined,
        airportName: airport.resource_name,
      };
    }
    return null;
  }, [selectedAirports]);

  // Initialize from backend saved options (once, on first load)
  useEffect(() => {
    if (loadingSettings || loadingFilters || initializedFromBackend) {
      return;
    }

    console.log('[GoldenWings] 🔄 Initializing from backend options...');
    console.log('[GoldenWings] 📦 Received savedSettings:', savedSettings);
    console.log('[GoldenWings] 📦 Received savedFilters:', savedFilters);

    // Apply saved view if available - use exact string as CalendarView
    if (savedSettings?.default_view) {
      console.log(
        '[GoldenWings] 👀 Found default_view in savedSettings:',
        savedSettings.default_view
      );
      const validViews: CalendarView[] = [
        '1day',
        '2day',
        '3day',
        '7day',
        'week-new',
        'month',
        'year',
        'year-agenda',
        'day-h-1h',
        'day-h-2d',
        'day-h-3d',
        'week-h',
      ];
      const isValid = validViews.includes(
        savedSettings.default_view as CalendarView
      );
      console.log(
        '[GoldenWings] 🔍 Is view valid?',
        isValid,
        'View value:',
        savedSettings.default_view
      );

      if (isValid) {
        console.log(
          '[GoldenWings] ✅ Setting view from backend:',
          savedSettings.default_view
        );
        setView(savedSettings.default_view as CalendarView);
      } else {
        console.warn(
          '[GoldenWings] ⚠️ View NOT valid, skipping. View was:',
          savedSettings.default_view
        );
      }
    } else {
      console.log('[GoldenWings] ℹ️ No default_view found in savedSettings');
    }

    // Apply saved resource filters if available
    if (savedFilters?.calendar_ids && savedFilters.calendar_ids.length > 0) {
      console.log(
        '[GoldenWings] Setting filters from backend:',
        savedFilters.calendar_ids
      );
      setSelectedResourceIds(savedFilters.calendar_ids);
    }

    // Apply saved showDayNight setting
    if (savedSettings?.show_day_night !== undefined) {
      console.log(
        '[GoldenWings] Setting showDayNight from backend:',
        savedSettings.show_day_night
      );
      setShowDayNight(savedSettings.show_day_night);
    }

    // Apply saved showWeather setting
    if (savedSettings?.show_weather !== undefined) {
      console.log(
        '[GoldenWings] Setting showWeather from backend:',
        savedSettings.show_weather
      );
      setShowWeather(savedSettings.show_weather);
    }

    // Apply saved filtersVisible setting
    if (savedSettings?.filters_visible !== undefined) {
      console.log(
        '[GoldenWings] Setting filtersVisible from backend:',
        savedSettings.filters_visible
      );
      setFiltersVisible(savedSettings.filters_visible);
    }

    // Apply saved collapsed groups setting
    if (savedSettings?.collapsed_groups) {
      console.log(
        '[GoldenWings] Setting collapsedGroups from backend:',
        savedSettings.collapsed_groups
      );
      setCollapsedGroups(savedSettings.collapsed_groups);
    }

    setInitializedFromBackend(true);
  }, [
    savedSettings,
    savedFilters,
    loadingSettings,
    loadingFilters,
    initializedFromBackend,
  ]);

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    const current = dayjs(currentDate);
    let start: string;
    let end: string;
    let numDays = 1;

    switch (view) {
      case 'day-h-1h':
      case '1day':
        numDays = 1;
        // Use same approach as 2-day view to catch events that span midnight
        // This ensures we get events that start on this day but end next day
        start = current.startOf('day').toISOString();
        end = current.add(1, 'day').startOf('day').toISOString();
        break;
      case '2day':
        numDays = 2;
        start = current.startOf('day').toISOString();
        end = current.add(1, 'day').endOf('day').toISOString();
        break;
      case '3day':
        numDays = 3;
        start = current.subtract(1, 'day').startOf('day').toISOString();
        end = current.add(1, 'day').endOf('day').toISOString();
        break;
      case '7day':
        numDays = 7;
        start = current.subtract(1, 'day').startOf('day').toISOString();
        end = current.add(5, 'day').endOf('day').toISOString();
        break;
      case 'week-new': {
        // Calculate Monday of current week
        const dayOfWeek = current.day();
        const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = current
          .subtract(mondayOffset, 'day')
          .startOf('day')
          .toISOString();
        end = current
          .subtract(mondayOffset, 'day')
          .add(6, 'day')
          .endOf('day')
          .toISOString();
        break;
      }
      case 'day-h-2d':
        numDays = 2;
        start = current.startOf('day').toISOString();
        end = current
          .add(numDays - 1, 'day')
          .endOf('day')
          .toISOString();
        break;
      case 'day-h-3d':
        numDays = 3;
        start = current.startOf('day').toISOString();
        end = current
          .add(numDays - 1, 'day')
          .endOf('day')
          .toISOString();
        break;
      case 'week-h':
        numDays = 7;
        start = current.startOf('day').toISOString();
        end = current
          .add(numDays - 1, 'day')
          .endOf('day')
          .toISOString();
        break;
      case 'month':
        start = current.startOf('month').subtract(7, 'days').toISOString();
        end = current.endOf('month').add(7, 'days').toISOString();
        break;
      default:
        start = current.startOf('month').toISOString();
        end = current.endOf('month').toISOString();
    }

    return { start_date: start, end_date: end };
  }, [currentDate, view]);

  // Fetch events for the calendar with filters
  // Use effectiveResourceIds which only includes groups with subset selection
  const {
    data: events = [],
    isLoading: loadingEvents,
    error: eventsError,
  } = useEvents(calendarId || '', {
    ...dateRange,
    resource_ids:
      effectiveResourceIds.length > 0 ? effectiveResourceIds : undefined,
  });

  // Fetch ALL events (no filters) for cross-filter count calculation in ResourceFilterPanel
  // Only fetch when filters are visible to avoid unnecessary API calls
  const { data: allEventsForCounting = [] } = useEvents(
    filtersVisible ? calendarId || '' : '',
    {
      ...dateRange,
      // No resource_ids filter - get all events
    }
  );

  const loading = loadingCalendars || loadingEvents;

  const handlePrevious = () => {
    const newDate = dayjs(currentDate);
    switch (view) {
      case 'day-h-1h':
      case '1day':
        setCurrentDate(newDate.subtract(1, 'day').toDate());
        break;
      case '2day':
      case 'day-h-2d':
        setCurrentDate(newDate.subtract(2, 'day').toDate());
        break;
      case '3day':
      case 'day-h-3d':
        setCurrentDate(newDate.subtract(3, 'day').toDate());
        break;
      case '7day':
      case 'week-h':
        setCurrentDate(newDate.subtract(7, 'day').toDate());
        break;
      case 'week-new':
        setCurrentDate(newDate.subtract(1, 'week').toDate());
        break;
      case 'month':
        setCurrentDate(newDate.subtract(1, 'month').toDate());
        break;
      case 'year':
        setCurrentDate(newDate.subtract(1, 'year').toDate());
        break;
    }
  };

  const handleNext = () => {
    const newDate = dayjs(currentDate);
    switch (view) {
      case 'day-h-1h':
      case '1day':
        setCurrentDate(newDate.add(1, 'day').toDate());
        break;
      case '2day':
      case 'day-h-2d':
        setCurrentDate(newDate.add(2, 'day').toDate());
        break;
      case '3day':
      case 'day-h-3d':
        setCurrentDate(newDate.add(3, 'day').toDate());
        break;
      case '7day':
      case 'week-h':
        setCurrentDate(newDate.add(7, 'day').toDate());
        break;
      case 'week-new':
        setCurrentDate(newDate.add(1, 'week').toDate());
        break;
      case 'month':
        setCurrentDate(newDate.add(1, 'month').toDate());
        break;
      case 'year':
        setCurrentDate(newDate.add(1, 'year').toDate());
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleToggleFilters = () => {
    const newValue = !filtersVisible;
    setFiltersVisible(newValue);

    // Persist filtersVisible change to backend
    console.log('[GoldenWings] Saving filtersVisible to backend:', newValue);
    saveSettingsMutation.mutate({
      ...savedSettings,
      filters_visible: newValue,
    });
  };

  const handleFilterChange = (resourceIds: string[]) => {
    setSelectedResourceIds(resourceIds);

    // Persist filter changes to backend
    console.log('[GoldenWings] Saving filters to backend:', resourceIds);
    saveFiltersMutation.mutate({
      ...savedFilters,
      calendar_ids: resourceIds,
    });
  };

  const handleCollapsedGroupsChange = (
    newCollapsed: FilterGroupCollapsedState
  ) => {
    setCollapsedGroups(newCollapsed);

    // Persist collapsed groups change to backend
    console.log(
      '[GoldenWings] Saving collapsedGroups to backend:',
      newCollapsed
    );
    saveSettingsMutation.mutate({
      ...savedSettings,
      collapsed_groups: newCollapsed,
    });
  };

  // Views that allow only single airport selection (vertical calendar views)
  const SINGLE_AIRPORT_VIEWS = ['1day', '2day', '3day', '7day', 'week-new'];

  const handleViewChange = (newView: CalendarView) => {
    console.log('[GoldenWings] handleViewChange called with:', newView);
    console.log('[GoldenWings] Current savedSettings:', savedSettings);
    setView(newView);

    // If switching to single-airport view and multiple airports are selected,
    // keep only the first one
    const isSwitchingToSingleAirportView =
      SINGLE_AIRPORT_VIEWS.includes(newView);
    if (isSwitchingToSingleAirportView && selectedAirports.length > 1) {
      // Get all airport IDs
      const airportIds = airports.map((a) => a.id);
      // Get currently selected airport IDs
      const selectedAirportIds = selectedResourceIds.filter((id) =>
        airportIds.includes(id)
      );
      // Keep only the first airport, remove others
      if (selectedAirportIds.length > 1) {
        const firstAirportId = selectedAirportIds[0];
        const nonAirportIds = selectedResourceIds.filter(
          (id) => !airportIds.includes(id)
        );
        const newSelection = [...nonAirportIds, firstAirportId];
        setSelectedResourceIds(newSelection);
        // Save to backend
        saveFiltersMutation.mutate({
          ...savedFilters,
          calendar_ids: newSelection,
        });
      }
    }

    // Persist EXACT view to backend (including horizontal variants)
    const newSettings = {
      ...savedSettings,
      default_view: newView as any,
    };
    console.log(
      '[GoldenWings] Saving view to backend:',
      newView,
      'Full settings:',
      newSettings
    );
    saveSettingsMutation.mutate(newSettings, {
      onSuccess: (data) => {
        console.log('[GoldenWings] ✅ View saved successfully:', data);
      },
      onError: (error) => {
        console.error('[GoldenWings] ❌ Failed to save view:', error);
      },
    });
  };

  const handleShowDayNightChange = (show: boolean) => {
    setShowDayNight(show);

    // Persist showDayNight change to backend
    console.log('[GoldenWings] Saving showDayNight to backend:', show);
    saveSettingsMutation.mutate({
      ...savedSettings,
      show_day_night: show,
    });
  };

  const handleShowWeatherChange = (show: boolean) => {
    setShowWeather(show);

    // Persist showWeather change to backend
    console.log('[GoldenWings] Saving showWeather to backend:', show);
    saveSettingsMutation.mutate({
      ...savedSettings,
      show_weather: show,
    });
  };

  const handleEventClick = (event: CalendarEvent) => {
    message.info(`Event clicked: ${event.title}`);
    // TODO: Open event details modal
  };

  const handleSlotClick = (
    date: Date,
    _resourceIdOrHour?: string | number,
    _hour?: number
  ) => {
    // Open event form modal with pre-populated time
    // Note: For vertical views, second param is hour; for horizontal views, it's resourceId
    setEventFormStartTime(date);
    setEventFormModalOpen(true);
  };

  const handleEventFormClose = () => {
    setEventFormModalOpen(false);
    setEventFormStartTime(undefined);
  };

  const handleEventFormSuccess = () => {
    // Refetch events after successful creation
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
  };

  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setView('1day');
  };

  // Show loading state
  if (loadingCalendars) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Show error if no calendars found
  if (!calendarId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="No Calendar Found"
          description="Please create a calendar first to start scheduling flights."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="goldenwings-calendar-page">
      <CalendarHeader
        view={view}
        currentDate={currentDate}
        onViewChange={handleViewChange}
        onDateChange={setCurrentDate}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onToday={handleToday}
        filtersVisible={filtersVisible}
        onToggleFilters={handleToggleFilters}
        activeFiltersCount={selectedResourceIds.length}
        selectedResourceIds={selectedResourceIds}
        allResources={allResources}
        showDayNight={showDayNight}
        onShowDayNightChange={handleShowDayNightChange}
        dayNightDisabled={dayNightDisabled}
        dayNightDisabledReason={dayNightDisabledReason}
        showWeather={showWeather}
        onShowWeatherChange={handleShowWeatherChange}
        weatherDisabled={weatherDisabled}
        weatherDisabledReason={weatherDisabledReason}
      />

      <div className="calendar-body">
        {filtersVisible && (
          <ResourceFilterPanel
            selectedResourceIds={selectedResourceIds}
            onSelectionChange={handleFilterChange}
            currentView={view}
            airplanes={airplanes}
            instructors={instructors}
            pilots={pilots}
            students={students}
            airports={airports}
            isLoading={loadingResources}
            events={allEventsForCounting}
            collapsedGroups={collapsedGroups}
            onCollapsedGroupsChange={handleCollapsedGroupsChange}
          />
        )}

        <div className="calendar-content">
          {eventsError && (
            <Alert
              message="Error Loading Events"
              description="Failed to load calendar events. Please try again."
              type="error"
              showIcon
              style={{ margin: 16 }}
            />
          )}
          {view === '1day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={0}
              dayCount={1}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === '2day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={0}
              dayCount={2}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === '3day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={-1}
              dayCount={3}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === '7day' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={-1}
              dayCount={7}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'week-new' && (
            <MultiDayVerticalView
              currentDate={currentDate}
              dayOffset={calculateMondayOffset(currentDate)}
              dayCount={7}
              events={events}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'day-h-1h' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={1}
              hourResolution={1}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              onDateChange={setCurrentDate}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'day-h-2d' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={2}
              hourResolution={1}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'day-h-3d' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={3}
              hourResolution={2}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'week-h' && (
            <HorizontalView
              currentDate={currentDate}
              events={events}
              numDays={7}
              hourResolution={4}
              loading={loading}
              onEventClick={handleEventClick}
              onSlotClick={handleSlotClick}
              onEventUpdate={async (eventId, newStartTime, newEndTime) => {
                await updateEventMutation.mutateAsync({
                  eventId,
                  startTime: newStartTime,
                  endTime: newEndTime,
                });
              }}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
              showWeather={showWeather}
              weatherCoordinates={weatherCoordinates}
              selectedAirports={selectedAirportsWithTimezone}
            />
          )}

          {view === 'month' && (
            <MonthlyView
              currentDate={currentDate}
              events={events}
              loading={loading}
              onDayClick={handleDayClick}
              onEventClick={handleEventClick}
              airportId={selectedAirportId}
              showDayNight={showDayNight}
            />
          )}

          {view === 'year' && (
            <YearView
              year={currentDate.getFullYear()}
              calendarId={calendarId}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              onDayClick={handleDayClick}
              onMonthClick={(date) => {
                setCurrentDate(date);
                setView('month');
              }}
            />
          )}

          {view === 'year-agenda' && (
            <YearlyAgendaView
              year={currentDate.getFullYear()}
              calendarId={calendarId}
              selectedResourceIds={selectedResourceIds}
              allResources={allResources}
              onEventClick={(event) => {
                console.log('Event clicked:', event);
                // TODO: Implement event detail modal
              }}
              onDayClick={handleDayClick}
            />
          )}
        </div>
      </div>

      {/* Event Form Modal */}
      <EventFormModal
        open={eventFormModalOpen}
        onClose={handleEventFormClose}
        onSuccess={handleEventFormSuccess}
        startTime={eventFormStartTime}
        departureAirportId={selectedAirportId}
      />
    </div>
  );
};
